import { Box, SxProps } from '@mui/material';
import { CSSProperties, FC, useState } from 'react';

// Uses legacy props (layout/objectFit/objectPosition/onLoadingComplete);
// next/legacy/image preserves the Next 12 <Image> behaviour after the Next 15 upgrade.
import Image from 'next/legacy/image';
import { MUIResponsiveValue } from '@/logic/types';
import { mediaImageLoader, supportsMediaResize } from '@/logic/helpers/media-image';
import MuviFallBackImage from '/public/icons/shared/logo-share-og.png';
import Skeleton from '@mui/material/Skeleton';

interface ImageBoxProps {
  img: string | File | FC;
  fallBackImg?: File | string;
  title?: string;
  width?: string | number;
  height?: string | number;
  maxWidth?: MUIResponsiveValue<string | number>;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none';
  sx?: SxProps;
  debug?: boolean;
  imageId?: string;
  style?: CSSProperties;
  objectPosition?: string | number;
  layout?: 'fill' | 'fixed' | 'intrinsic' | 'responsive';
  /**
   * Passed straight to next/legacy/image. Without it, the browser treats the
   * image as 100vw and can pick a full-width variant from the srcset even
   * when the rendered slot is a fraction of that (a grid poster, a carousel
   * slide) — oversized downloads, worse on a throttled mobile connection.
   */
  sizes?: string;
  /**
   * Opt in for images that are the LCP candidate (above the fold on first
   * paint). Every other image keeps `loading='lazy'`, which is right for them
   * and fatal for the LCP one — the browser will not request it until layout
   * has run.
   *
   * Confirmed by reading the rendered DOM: next/legacy/image emits its own
   * `<link rel="preload">` for the image rather than deferring it behind its
   * IntersectionObserver. It sets no `loading` or `fetchpriority` attribute on
   * the `<img>` itself, so do not go looking for those — the preload link is
   * the whole mechanism.
   *
   * It also starts without the loading skeleton, since an LCP image has nothing
   * to hide behind. (The skeleton no longer unmounts and remounts the `<img>`
   * for the non-priority case either — it overlays instead. See the render.)
   *
   * That preload is part of the server-rendered <head> (it carries
   * `data-next-head`), with the image's own srcset and `sizes`, so do not add a
   * hand-written preload for the same image: the browser fetches both.
   */
  priority?: boolean;
}

export const ImageBoxFuture: FC<ImageBoxProps> = ({
  img,
  fallBackImg,
  title = '',
  width,
  height,
  sx,
  objectFit,
  maxWidth,
  debug = true,
  layout,
  style,
  objectPosition,
  imageId,
  priority = false,
  sizes,
}) => {
  const [loading, setLoading] = useState<boolean>(!priority);
  const [onErrorSrc, setOnErrorSrc] = useState<string | File | undefined>(undefined);

  const src = (onErrorSrc || img) as string;

  const handleOnError = (e: React.SyntheticEvent<HTMLImageElement, Event>): void => {
    e?.currentTarget?.src !== MuviFallBackImage.src &&
      setOnErrorSrc(fallBackImg || MuviFallBackImage.src);
  };

  return (
    <Box
      component='figure'
      sx={{
        position: 'relative',
        lineHeight: 0,
        objectFit,
        maxWidth,
        userSelect: 'none',
        ...sx,
      }}
    >
      {/*
        The skeleton is a sibling overlay, not a parent. It used to wrap the
        <Image>, which had two consequences:

          - MUI's Skeleton applies `& > *: { visibility: hidden }` to its
            children, so the <img>'s alt text was pulled out of the
            accessibility tree until the image loaded. Lazy images below the
            fold never load, so anything whose only content was one of these —
            the footer's social links — computed an empty accessible name.
          - the image lived in two mutually exclusive branches, so it was
            unmounted and remounted on load, making the browser paint it twice.

        Overlaying instead keeps a single <img> in the DOM for its whole life.
      */}
      {loading && (
        <Skeleton
          variant='rectangular'
          animation='wave'
          // Decorative — the <img> underneath carries the accessible name.
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            zIndex: debug ? 1 : 'auto',
          }}
        />
      )}
      <Image
        {...(imageId ? { id: imageId } : {})}
        alt={title}
        src={src}
        width={width as number}
        height={height as number}
        onLoadingComplete={() => setLoading(false)}
        onError={e => handleOnError(e)}
        layout={layout || 'fill'}
        style={style}
        sizes={sizes}
        // Resized by the media CDN when its host supports it; everything else
        // is requested as-is. See media-image.ts.
        loader={mediaImageLoader}
        unoptimized={!supportsMediaResize(src)}
        objectPosition={objectPosition}
        objectFit={onErrorSrc ? 'contain' : objectFit}
        // next/legacy/image rejects `priority` and `loading` together.
        {...(priority ? { priority: true } : { loading: 'lazy' as const })}
      />
    </Box>
  );
};

export default ImageBoxFuture;
